import type { Stage } from '@/api/domains';
import { cn } from '@/lib/utils';

/**
 * The DMARC enforcement journey as four steps (No record → p=none → quarantine → reject). Each step
 * is labelled in the tooltip and the current one is named beside it, so it never relies on colour.
 */
const STEPS: { stage: Stage; label: string }[] = [
  { stage: 'missing', label: 'No DMARC' },
  { stage: 'monitoring', label: 'p=none' },
  { stage: 'quarantine', label: 'Quarantine' },
  { stage: 'reject', label: 'Reject' },
];

export function Journey({ stage, showLabel = true }: { stage: Stage; showLabel?: boolean }) {
  const at = STEPS.findIndex((s) => s.stage === stage);
  return (
    <span className="inline-flex shrink-0 items-center gap-2" title={`DMARC: ${STEPS[at].label}`}>
      <span className="inline-flex items-center gap-0.5">
        {STEPS.map((s, i) => (
          <span key={s.stage} title={s.label}
            className={cn('h-1.5 w-4 rounded-full', i > at ? 'bg-muted' : stage === 'reject' ? 'bg-success' : stage === 'missing' ? 'bg-destructive' : i === at ? 'bg-warning' : 'bg-success/60')} />
        ))}
      </span>
      {showLabel && <span className={cn('whitespace-nowrap text-xs font-medium', stage === 'reject' ? 'text-success' : stage === 'missing' ? 'text-destructive' : 'text-foreground')}>{STEPS[at].label}</span>}
    </span>
  );
}

export function Pct({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className={cn('text-xs font-medium tabular-nums', value >= 98 ? 'text-success' : value >= 90 ? 'text-warning' : 'text-destructive')}>{value}%</span>;
}

export function Ok({ ok, label }: { ok: boolean | null; label: string | null }) {
  if (ok == null) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className={cn('text-xs', ok ? 'text-success' : 'text-destructive')} title={label ?? undefined}>{ok ? 'OK' : 'Problem'}</span>;
}
